
import type { Language, ShoppingCategory } from '../types';
import { detectCategoryFromName } from './shoppingCategories';
import { decimalToFractionLabel, decimalToVulgarFractionLabel, formatQuantityDisplay, formatQuantityVulgarDisplay } from './fractionQuantity';

// Approximate kitchen conversions (standard for shopping lists)
const CONVERSIONS: Record<string, number> = {
    'g': 1,
    'kg': 1000,
    'ml': 1,
    'liter': 1000,
    'l': 1000,
    'tsp': 5,
    'level_tsp': 5,
    'tbsp': 15,
    'heaping_tbsp': 25, // Approx 25ml/g for heaping
    'cup': 240,
    'pinch': 0.5,
    'tablespoon': 15,
    'teaspoon': 5,
    'oz': 28.35,
    'lb': 453.59,
    'fl oz': 29.57,
    'pint': 473,
    'quart': 946,
    'gallon': 3785,
    'גרם': 1,
    'ק"ג': 1000,
    'קג': 1000,
    'קילו': 1000,
    'קילוגרם': 1000,
    'מ"ל': 1,
    'מל': 1,
    'ליטר': 1000,
    'כפית': 5,
    'כפית שטוחה': 5,
    'כפיות שטוחות': 5,
    'כפות': 15,
    'כפיות': 5,
    'כף': 15,
    'כף גדושה': 25,
    'כפות גדושות': 25,
    'כוס': 240,
    'קורט': 0.5,
    'שוט': 30,
    'unit': 1,
    'יחידה': 1,
    'יחידות': 1,
    'ראש': 1,
    'שן': 1,
    'שיניים': 1,
    'חבילה': 1,
    'שקית': 1,
    'סלסלה': 1,
    'סלסלאות': 1,
    'basket': 1,
    'baskets': 1,
    'clove': 1,
    'head': 1,
    'tub': 1,
    'handful': 1,
    'גביע': 1,
    'חופן': 1
};

const CANONICAL_MAP: Record<string, string> = {
    'מים': 'water', 'water': 'water',
    'מלח': 'salt', 'salt': 'salt',
    'סוכר': 'sugar', 'sugar': 'sugar',
    'אבקת אפייה': 'baking powder', 'baking powder': 'baking powder',
    'שמן': 'oil', 'oil': 'oil',
    'חמאה': 'butter', 'butter': 'butter',
    'קמח': 'flour', 'flour': 'flour',
    'ביצה': 'eggs', 'ביצים': 'eggs', 'egg': 'eggs', 'eggs': 'eggs'
};

const PLACEHOLDER_UNITS = new Set(['__unit__', '_unit_', 'unit_', 'units', 'undefined', 'null']);

export const unifyUnit = (unit: string): string => {
    let u = (unit || '').toLowerCase().trim();

    if (!u || PLACEHOLDER_UNITS.has(u)) return 'unit';
    
    // Normalize Hebrew quotes/apostrophes
    u = u.replace(/״/g, '"').replace(/׳/g, "'");

    // Weight
    if (['kg', 'kilogram', 'kgs', 'ק"ג', 'קג', 'קילו', 'קילוגרם'].includes(u)) return 'kg';
    if (['g', 'gram', 'gr', 'gms', 'grams', 'גרם'].includes(u)) return 'g';
    if (['oz', 'ounce', 'ounces'].includes(u)) return 'oz'; // We convert oz to g usually, but keep unit id distinct here
    if (['lb', 'lbs', 'pound', 'pounds'].includes(u)) return 'lb';

    // Volume
    if (['l', 'liter', 'litre', 'liters', 'ליטר', 'ליטרים'].includes(u)) return 'liter';
    if (['ml', 'milliliter', 'mls', 'מ"ל', 'מל'].includes(u)) return 'ml';
    if (['cup', 'cups', 'c', 'כוס', 'כוסות'].includes(u)) return 'cup';
    if (['heaping tbsp', 'heaping tablespoon', 'heaping tablespoons', 'כף גדושה', 'כפות גדושות'].includes(u)) return 'heaping_tbsp';
    if (['tbsp', 'tablespoon', 'tablespoons', 'tb', 'tbs', 'כף', 'כפות', 'spoon', 'spoons'].includes(u)) return 'tbsp';
    if (['tsp', 'teaspoon', 'teaspoons', 'ts', 'כפית', 'כפיות'].includes(u)) return 'tsp';
    if (['level tsp', 'flat tsp', 'כפית שטוחה', 'כפיות שטוחות'].includes(u)) return 'level_tsp';
    if (['fl oz', 'fluid ounce'].includes(u)) return 'fl oz';
    
    // Count
    if (['unit', 'units', 'ea', 'each', 'יחידה', 'יחידות'].includes(u)) return 'unit';
    
    // Containers/Packs
    if (['can', 'cans', 'פחית', 'פחיות', 'קופסא', 'קופסאות'].includes(u)) return 'can';
    if (['package', 'pkg', 'pkgs', 'packages', 'חבילה', 'חבילות', 'carton', 'cartons'].includes(u)) return 'package';
    if (['basket', 'baskets', 'סלסלה', 'סלסלאות'].includes(u)) return 'basket';
    if (['bag', 'bags', 'שקית', 'שקיות'].includes(u)) return 'bag';
    if (['bottle', 'bottles', 'בקבוק', 'בקבוקים'].includes(u)) return 'bottle';
    if (['jar', 'jars', 'צנצנת', 'צנצנות'].includes(u)) return 'jar';
    if (['container', 'containers', 'מכל', 'מיכל'].includes(u)) return 'container';
    if (['tub', 'tubs', 'גביע', 'גביעים'].includes(u)) return 'tub';
    if (['handful', 'handfuls', 'חופן', 'חופנים'].includes(u)) return 'handful';
    
    // Misc
    if (['clove', 'cloves', 'שן', 'שיניים'].includes(u)) return 'clove';
    if (['head', 'heads', 'ראש', 'ראשים'].includes(u)) return 'head';
    if (['stalk', 'stalks', 'גבעול', 'גבעולים'].includes(u)) return 'stalk';
    if (['slice', 'slices', 'פרוסה', 'פרוסות'].includes(u)) return 'slice';

    if (['לבחירה', 'לבחירתך', 'כרצונך', 'to choose', 'choose', 'optional', 'as desired'].includes(u)) return 'to_choose';

    if (['מעט', 'a little', 'little'].includes(u)) return 'a_little';
    if (['שלם', 'whole'].includes(u)) return 'whole';
    if (['חצי', 'half'].includes(u)) return 'half';
    if (['רבע', 'quarter'].includes(u)) return 'quarter';
    if (['שליש', 'third'].includes(u)) return 'third';
    if (u.endsWith(' שלם') || u.endsWith(' whole')) return 'whole';
    
    return u; 
};

export const translateUnitLabel = (unit: string, t: (key: string) => string): string => {
    const canonical = unifyUnit(unit);
    const primaryKey = `unit_${canonical}`;
    let translated = t(primaryKey);
    if (translated !== primaryKey) return translated;

    const raw = (unit || '').trim();
    if (raw && raw.toLowerCase() !== canonical) {
        const rawKey = `unit_${raw}`;
        translated = t(rawKey);
        if (translated !== rawKey) return translated;
    }

    if (canonical === 'unit') {
        const countLabel = t('unit_unit');
        if (countLabel !== 'unit_unit') return countLabel;
    }

    return raw || canonical;
};

export const QUALITATIVE_UNITS = ['a_little', 'whole', 'half', 'quarter', 'third'] as const;

export const isQualitativeUnit = (unit: string): boolean =>
    (QUALITATIVE_UNITS as readonly string[]).includes(unifyUnit(unit));

export const isNumberlessUnit = (unit: string): boolean => {
    const u = unifyUnit(unit);
    const unmeasured = ['to taste', 'as needed', 'to_choose', 'לפי הטעם', 'לפי הצורך'];
    return unmeasured.includes(u) || isQualitativeUnit(u);
};

export const getCanonicalName = (name: string): string => {
    const clean = (name || '').toLowerCase().trim();
    if (CANONICAL_MAP[clean]) return CANONICAL_MAP[clean];
    if (clean.includes('שמן') || clean.includes('oil')) return 'oil';
    if (clean.includes('בצל') || clean.includes('onion')) return 'onion';
    if (clean.includes('שום') || clean.includes('garlic')) return 'garlic';
    return clean;
};

/** Everyday pantry staples omitted from weekly/monthly planning lists. */
const ORDINARY_CANONICAL = new Set<string>(['water', 'oil', 'salt', 'sugar']);

const matchesOrdinaryPattern = (clean: string): boolean =>
    /מים|water/i.test(clean)
    || /שמן|\boil\b/i.test(clean)
    || /מלח|\bsalt\b/i.test(clean)
    || /סוכר|\bsugar\b/i.test(clean);

export const isOrdinaryShoppingItem = (nameOrCanonical: string): boolean => {
    if (!nameOrCanonical) return false;
    if (ORDINARY_CANONICAL.has(nameOrCanonical)) return true;

    const canonical = getCanonicalName(nameOrCanonical);
    if (ORDINARY_CANONICAL.has(canonical)) return true;

    const clean = nameOrCanonical.toLowerCase().trim();
    return matchesOrdinaryPattern(clean);
};

export const detectCategory = (name: string, isMeat: boolean): ShoppingCategory =>
    detectCategoryFromName(name, isMeat);

export const normalizeQuantity = (quantity: number, unit: string, name?: string): { normalizedQty: number, isVolume: boolean } => {
    const u = unifyUnit(unit);
    const factor = CONVERSIONS[u] || 1;
    const normalizedQty = quantity * factor;
    
    const volumeUnits = ['ml', 'liter', 'l', 'tsp', 'level_tsp', 'tbsp', 'heaping_tbsp', 'cup', 'fl oz', 'pint', 'quart', 'gallon', 'מ"ל', 'מל', 'ליטר', 'כפית', 'כפית שטוחה', 'כף', 'כוס', 'כף גדושה'];
    const isVolume = volumeUnits.includes(u);

    return { normalizedQty, isVolume };
};

export const formatIngredientQuantity = (quantity: number, unit: string, t: (k: string) => string): string => {
    const u = unifyUnit(unit);

    if (isNumberlessUnit(u) || quantity <= 0) return translateUnitLabel(u, t);

    const fractionLabel = decimalToFractionLabel(quantity);

    if (u === 'g') {
        if (quantity >= 1000) {
            const kgQty = quantity / 1000;
            const kgLabel = decimalToFractionLabel(kgQty) ?? parseFloat(kgQty.toFixed(2));
            return `${kgLabel} ${t('unit_kg')}`;
        }
        return `${parseFloat(quantity.toFixed(0))} ${t('unit_g')}`;
    }

    if (u === 'ml') {
        if (quantity >= 1000) {
            const literQty = quantity / 1000;
            const literLabel = decimalToFractionLabel(literQty) ?? parseFloat(literQty.toFixed(2));
            return `${literLabel} ${t('unit_liter')}`;
        }
        return `${parseFloat(quantity.toFixed(0))} ${t('unit_ml')}`;
    }

    const qtyLabel = fractionLabel ?? formatQuantityDisplay(quantity);
    return `${qtyLabel} ${translateUnitLabel(u, t)}`;
};

export const formatCookingIngredientQuantity = (quantity: number, unit: string, t: (k: string) => string): string => {
    const parts = formatCookingIngredientQuantityParts(quantity, unit, t);
    if (!parts.amount) return parts.unit;
    return `${parts.amount} ${parts.unit}`;
};

export type CookingQuantityParts = {
    amount: string;
    unit: string;
};

export const formatCookingIngredientQuantityParts = (
    quantity: number,
    unit: string,
    t: (k: string) => string,
): CookingQuantityParts => {
    const u = unifyUnit(unit);

    if (isNumberlessUnit(u) || quantity <= 0) {
        return { amount: '', unit: translateUnitLabel(u, t) };
    }

    const toLabel = (value: number): string =>
        decimalToVulgarFractionLabel(value) ?? formatQuantityVulgarDisplay(value);

    if (u === 'g') {
        if (quantity >= 1000) {
            return { amount: toLabel(quantity / 1000), unit: t('unit_kg') };
        }
        return { amount: String(parseFloat(quantity.toFixed(0))), unit: t('unit_g') };
    }

    if (u === 'ml') {
        if (quantity >= 1000) {
            return { amount: toLabel(quantity / 1000), unit: t('unit_liter') };
        }
        return { amount: String(parseFloat(quantity.toFixed(0))), unit: t('unit_ml') };
    }

    return { amount: toLabel(quantity), unit: translateUnitLabel(u, t) };
};

export const formatConsolidatedQuantity = (quantity: number, unit: string, t: (k: any) => string, name?: string): string =>
    formatIngredientQuantity(quantity, unit, t);
